import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { db } from '../../firebase';
import { doc, getDoc, updateDoc, setDoc, serverTimestamp, collection, query, where, onSnapshot, getDocs } from 'firebase/firestore';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Divider,
  CircularProgress,
  useMediaQuery,
  Collapse,
} from '@mui/material';
import { styled } from '@mui/system';
import Alerts from '../../components/Alerts';

const StyledPaper = styled(Paper)(({ theme, darkMode }) => ({
  padding: theme.spacing(3),
  borderRadius: '12px',
  boxShadow: darkMode ? '0 4px 20px rgba(0,0,0,0.5)' : '0 4px 20px rgba(0,0,0,0.1)',
  background: darkMode ? 'linear-gradient(145deg, #2A2A40, #1e1e2f)' : 'linear-gradient(145deg, #ffffff, #f9fafb)',
  maxWidth: '600px',
  margin: 'auto',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
  },
}));

const StyledButton = styled(Button)(({ theme, darkMode }) => ({
  borderRadius: '8px',
  padding: theme.spacing(1.5, 3),
  fontWeight: 600,
  textTransform: 'none',
  boxShadow: darkMode ? '0 2px 8px rgba(0,0,0,0.5)' : '0 2px 10px rgba(0,0,0,0.1)',
  backgroundColor: darkMode ? '#4b5563' : '#735DA5',
  color: '#ffffff',
  '&:hover': {
    boxShadow: darkMode ? '0 4px 12px rgba(0,0,0,0.7)' : '0 4px 16px rgba(0,0,0,0.15)',
    backgroundColor: darkMode ? '#6b7280' : '#a583f2',
  },
}));

const StyledListItem = styled(ListItem)(({ theme, darkMode }) => ({
  borderRadius: '8px',
  marginBottom: theme.spacing(1),
  backgroundColor: darkMode ? '#3a3a5c' : '#fafafa',
  '&:hover': {
    backgroundColor: darkMode ? '#4b4b6e' : '#f0f0f0',
  },
}));

const CashPayment = ({ darkMode }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location?.state;
  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, severity: 'info', message: '' });
  const [receivedAmount, setReceivedAmount] = useState('');
  const [amountError, setAmountError] = useState(null);
  const [companyName, setCompanyName] = useState(null);
  const [assignedTableIds, setAssignedTableIds] = useState([]);
  const isMobile = useMediaQuery('(max-width:600px)');
  /*const userId = state?.userId || localStorage.getItem('userId') || null;
  const companyCode = location?.search ? new URLSearchParams(location.search).get('code') : null || null;
  const unitCode = state?.unitCode || (location?.search ? new URLSearchParams(location.search).get('unit') : null) || null;*/

  const params = new URLSearchParams(location.search);

const companyCode =
  state?.companyCode ||
  params.get('code') ||
  sessionStorage.getItem('companyCode') ||
  localStorage.getItem('companyCode');

const userId =
  state?.userId ||
  sessionStorage.getItem('userId') ||
  localStorage.getItem('userId');

const unitCode =
  state?.unitCode ||
  params.get('unit') ||
  sessionStorage.getItem('unitCode') ||
  localStorage.getItem('unitCode');
  const handleCloseSnackbar = () => {
    setSnackbar({ open: false, severity: snackbar.severity, message: '' });
  };

  const fetchCompanyName = async () => {
    if (!companyCode) {
      setCompanyName('Unknown Company');
      console.log('CashPayment: No companyCode for fetching company name');
      return;
    }
    try {
      const companyDocRef = doc(db, 'units', unitCode);
      const companyDoc = await getDoc(companyDocRef);
      if (companyDoc.exists()) {
        const companyData = companyDoc.data();
        setCompanyName(companyData.companyName || 'Unknown Company');
        console.log('CashPayment: Company details fetched:', { name: companyData.companyName, companyCode });
      } else {
        setCompanyName('Unknown Company');
        console.log('CashPayment: No company found for unitCode:', unitCode);
      }
    } catch (err) {
      console.error('CashPayment: Error fetching company details:', err);
      setCompanyName('Unknown Company');
    }
  };

  const fetchAssignedTables = async () => {
    if (!companyCode || !userId || !unitCode) {
      console.warn('CashPayment: Missing parameters for fetching assigned tables', { companyCode, userId, unitCode });
      return [];
    }
    try {
      const tablesQuery = query(
        collection(db, 'tables'),
        where('companyCode', '==', companyCode),
        where('unitCode', '==', unitCode),
        where('assignedServer', '==', userId)
      );
      const tablesSnapshot = await getDocs(tablesQuery);
      const tableIds = tablesSnapshot.docs.map(doc => doc.data().tableId).filter(id => id); // Filter out undefined/null
      console.log('CashPayment: Fetched assigned tables:', { userId, unitCode, tableIds });
      return tableIds;
    } catch (error) {
      console.error('CashPayment: Error fetching assigned tables:', { userId, unitCode, error });
      setSnackbar({
        open: true,
        severity: 'error',
        message: `Failed to fetch assigned tables: ${error.message}`,
      });
      return [];
    }
  };

  useEffect(() => {
    console.log('CashPayment: Initial state:', { state, userId, companyCode, unitCode, companyName, query: location?.search });
    if (!companyCode || !userId || !unitCode) {
      console.error('CashPayment: Missing required parameters, redirecting to login', { userId, companyCode, unitCode, query: location?.search });
      setSnackbar({
        open: true,
        severity: 'error',
        message: 'Authentication or unit required. Redirecting to login...',
      });
      setTimeout(() => navigate('/login'), 2000);
      return;
    }

    let unsubscribe = () => {};
    const fetchPendingOrders = async () => {
      try {
        const tableIds = await fetchAssignedTables();
        setAssignedTableIds(tableIds);
        if (tableIds.length === 0) {
          console.warn('CashPayment: No tables assigned to server for unit:', { userId, unitCode });
          setOrders([]);
          setSnackbar({
            open: true,
            severity: 'info',
            message: 'No tables assigned to server for this unit.',
          });
          setLoading(false);
          return () => {};
        }

        const q = query(
          collection(db, 'orders'),
          where('companyCode', '==', companyCode),
          where('unitCode', '==', unitCode),
          where('paymentStatus', '==', 'pending_confirmation'),
          where('paymentMethod', '==', 'cash'),
          where('tableId', 'in', tableIds.length > 0 ? tableIds : ['__dummy__']) // Ensure valid 'in' query
        );

        unsubscribe = onSnapshot(q, (snapshot) => {
          try {
            const pendingOrders = snapshot.docs
              .map(doc => ({ id: doc.id, ...doc.data() }))
              .filter(order => tableIds.includes(order.tableId) && order.tableId); // Ensure tableId exists
            console.log('CashPayment: Fetched pending orders:', pendingOrders.map(o => ({
              id: o.id,
              tableId: o.tableId,
              unitCode: o.unitCode,
              totalAmount: o.totalAmount,
              paymentStatus: o.paymentStatus,
              paymentMethod: o.paymentMethod,
            })));
            setOrders(pendingOrders);
            setLoading(false);
            if (pendingOrders.length === 0 && tableIds.length > 0) {
              setSnackbar({
                open: true,
                severity: 'info',
                message: 'No pending cash payments for assigned tables in this unit.',
              });
            }
          } catch (err) {
            console.error('CashPayment: Error processing snapshot:', err);
            setSnackbar({
              open: true,
              severity: 'error',
              message: 'Failed to load pending orders: ' + err.message,
            });
            setLoading(false);
          }
        }, (err) => {
          console.error('CashPayment: Error fetching pending orders:', err);
          setSnackbar({
            open: true,
            severity: 'error',
            message: 'Failed to load pending orders: ' + err.message,
          });
          setLoading(false);
        });

        await fetchCompanyName();
      } catch (err) {
        console.error('CashPayment: Error setting up listener:', err);
        setSnackbar({
          open: true,
          severity: 'error',
          message: 'Failed to initialize payments: ' + err.message,
        });
        setLoading(false);
      }

      return unsubscribe;
    };

    fetchPendingOrders().then(unsub => {
      unsubscribe = unsub;
    });

    return () => unsubscribe();
  }, [companyCode, userId, unitCode, navigate]);

  const handleSelectOrder = async (orderId) => {
    if (selectedOrder && selectedOrder.id === orderId) {
      setSelectedOrder(null);
      setReceivedAmount('');
      setAmountError(null);
      return;
    }

    try {
      console.log('CashPayment: Fetching order details for ID:', orderId, 'unitCode:', unitCode);
      const orderRef = doc(db, 'orders', orderId);
      const orderSnap = await getDoc(orderRef);
      if (orderSnap.exists()) {
        const orderData = { id: orderId, ...orderSnap.data() };
        console.log('CashPayment: Order details fetched:', orderData);
        setSelectedOrder(orderData);
        setSnackbar({ open: false, severity: 'info', message: '' });
      } else {
        console.error('CashPayment: Order not found for ID:', orderId);
        setSnackbar({
          open: true,
          severity: 'error',
          message: 'Selected order not found in database.',
        });
      }
    } catch (err) {
      console.error('CashPayment: Error fetching order details:', err.message, err.code);
      setSnackbar({
        open: true,
        severity: 'error',
        message: 'Failed to load order details: ' + err.message,
      });
    }
  };

  const handleAmountChange = (e) => {
    const value = e.target.value;
    if (/^\d*\.?\d*$/.test(value)) {
      setReceivedAmount(value);
      setAmountError(null);
    }
  };

  const confirmPayment = async () => {
    if (!selectedOrder) {
      setSnackbar({
        open: true,
        severity: 'error',
        message: 'No order selected.',
      });
      return;
    }

    const amount = parseFloat(receivedAmount);
    const totalAmount = selectedOrder.totalAmount || 0;

    if (isNaN(amount) || amount < totalAmount) {
      setAmountError(`Received amount must be at least ₹${totalAmount.toFixed(2)}.`);
      return;
    }

    try {
      console.log('CashPayment: Confirming payment for orderId:', selectedOrder.id, 'unitCode:', unitCode);
      const paymentId = `cash_${selectedOrder.id}_${Date.now()}`;
      const orderRef = doc(db, 'orders', selectedOrder.id);

      await updateDoc(orderRef, {
        paid: true,
        paymentStatus: 'completed',
        paymentId: paymentId,
        paymentMethod: 'cash',
        updatedAt: new Date(),
      });
      console.log(`CashPayment: Order ${selectedOrder.id} updated. PaymentId: ${paymentId}`);

      const paymentDocId = `payment_${selectedOrder.id}_${Date.now()}`;
      const paymentData = {
        orderId: selectedOrder.id,
        tableId: selectedOrder.tableId || 'N/A',
        companyCode: companyCode || 'N/A',
        unitCode: unitCode || 'N/A',
        date: serverTimestamp(),
        amount: totalAmount,
        paymentMethod: 'cash',
        paymentId: paymentId,
      };
      const paymentRef = doc(db, 'payments', paymentDocId);
      await setDoc(paymentRef, paymentData);
      console.log('CashPayment: Payment record created successfully:', paymentDocId, paymentData);

      setSnackbar({
        open: true,
        severity: 'success',
        message: `Cash payment for Order ${selectedOrder.id} confirmed successfully.`,
      });
      setSelectedOrder(null);
      setReceivedAmount('');
      setAmountError(null);
    } catch (err) {
      console.error('CashPayment: Error confirming payment:', err.message, err.code, err);
      setSnackbar({
        open: true,
        severity: 'error',
        message: `Failed to confirm payment or save to payments collection: ${err.message}`,
      });
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4, bgcolor: darkMode ? '#1e1e2f' : '#f9fafb' }}>
        <CircularProgress sx={{ color: darkMode ? '#93c5fd' : '#735DA5' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ py: 4, px: isMobile ? 2 : 4, minHeight: '100vh', bgcolor: darkMode ? '#1e1e2f' : '#f9fafb' }}>
      <StyledPaper elevation={3} darkMode={darkMode}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, textAlign: 'center', mb: 3, color: darkMode ? '#d1d5db' : '#1f2937' }}>
          Pending Cash Payments - {companyName} (Unit: {unitCode || 'N/A'})
        </Typography>
        <Alerts snackbar={snackbar} handleClose={handleCloseSnackbar} darkMode={darkMode} />
        {orders.length === 0 && !snackbar.open && (
          <Typography variant="body1" color={darkMode ? '#9ca3af' : 'text.secondary'} sx={{ textAlign: 'center', mb: 2 }}>
            No pending cash payments for this unit.
          </Typography>
        )}
        <List>
          {orders.map((order) => (
            <React.Fragment key={order.id}>
              <StyledListItem darkMode={darkMode}>
                <ListItemButton onClick={() => handleSelectOrder(order.id)}>
                  <ListItemText
                    primary={`Table: ${order.tableId || 'N/A'} | Order ID: ${order.id}`}
                    secondary={`Unit: ${order.unitCode || 'N/A'} | Total: ₹${(order.totalAmount || 0).toFixed(2)}`}
                    primaryTypographyProps={{ color: darkMode ? '#d1d5db' : '#1f2937' }}
                    secondaryTypographyProps={{ color: darkMode ? '#9ca3af' : 'text.secondary' }}
                  />
                </ListItemButton>
              </StyledListItem>
              <Collapse in={selectedOrder && selectedOrder.id === order.id}>
                <Box sx={{ p: 2, bgcolor: darkMode ? '#3a3a5c' : '#fff', borderRadius: '8px', mt: 1, mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: darkMode ? '#d1d5db' : '#1f2937' }}>
                    Order Details
                  </Typography>
                  <Typography variant="body1" sx={{ color: darkMode ? '#d1d5db' : '#1f2937' }}>
                    Table: {order.tableId || 'N/A'}
                  </Typography>
                  <Typography variant="body1" sx={{ color: darkMode ? '#d1d5db' : '#1f2937' }}>
                    Unit: {order.unitCode || 'N/A'}
                  </Typography>
                  <Typography variant="body1" sx={{ color: darkMode ? '#d1d5db' : '#1f2937' }}>
                    Order ID: {order.id}
                  </Typography>
                  <Typography variant="body1" sx={{ color: darkMode ? '#d1d5db' : '#1f2937' }}>
                    Payment Status: {order.paymentStatus || 'N/A'}
                  </Typography>
                  <Typography variant="subtitle1" sx={{ mt: 2, fontWeight: 500, color: darkMode ? '#d1d5db' : '#1f2937' }}>
                    Ordered Items:
                  </Typography>
                  {order.items.map((item, i) => (
                    <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, color: darkMode ? '#d1d5db' : '#1f2937' }}>
                      <Typography variant="body2">
                        {item.name} (x {item.quantity})
                      </Typography>
                      <Typography variant="body2">
                        ₹{(item.totalPrice || item.quantity * item.price).toFixed(2)}
                      </Typography>
                    </Box>
                  ))}
                  <Divider sx={{ my: 2, bgcolor: darkMode ? '#9ca3af' : '#e5e7eb' }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: darkMode ? '#d1d5db' : '#1f2937' }}>
                      Total Amount:
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: darkMode ? '#a583f2' : '#735DA5' }}>
                      ₹{(order.totalAmount || 0).toFixed(2)}
                    </Typography>
                  </Box>
                  {!order.paid && (
                    <Box sx={{ mt: 2 }}>
                      <TextField
                        label="Received Amount (₹)"
                        variant="outlined"
                        value={receivedAmount}
                        onChange={handleAmountChange}
                        fullWidth
                        type="text"
                        inputProps={{ inputMode: 'decimal', pattern: '[0-9]*\\.?[0-9]*' }}
                        error={!!amountError}
                        helperText={amountError}
                        sx={{
                          mb: 2,
                          bgcolor: darkMode ? '#4b4b6e' : '#fff',
                          '& .MuiInputBase-input': { color: darkMode ? '#d1d5db' : '#1f2937' },
                          '& .MuiInputLabel-root': { color: darkMode ? '#9ca3af' : '#64748b' },
                        }}
                      />
                      {receivedAmount && parseFloat(receivedAmount) >= order.totalAmount && (
                        <Typography variant="body2" color={darkMode ? '#9ca3af' : 'text.secondary'}>
                          Change to Return: ₹{(parseFloat(receivedAmount) - order.totalAmount).toFixed(2)}
                        </Typography>
                      )}
                      <Box sx={{ display: 'flex', gap: 2, mt: 3, flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
                        <StyledButton
                          variant="contained"
                          color="primary"
                          onClick={confirmPayment}
                          disabled={!receivedAmount || parseFloat(receivedAmount) < order.totalAmount || order.paid}
                          fullWidth={isMobile}
                          darkMode={darkMode}
                        >
                          {order.paid ? 'Payment Confirmed' : 'Confirm Payment'}
                        </StyledButton>
                        <StyledButton
                          variant="outlined"
                          color="primary"
                          onClick={() => {
                            setSelectedOrder(null);
                            setReceivedAmount('');
                            setAmountError(null);
                          }}
                          fullWidth={isMobile}
                          darkMode={darkMode}
                        >
                          Cancel
                        </StyledButton>
                      </Box>
                    </Box>
                  )}
                </Box>
              </Collapse>
            </React.Fragment>
          ))}
        </List>
        <StyledButton
          variant="outlined"
          color="primary"
          onClick={() => navigate('/servers/orders', { state: { companyCode, unitCode, userId,  tableId: sessionStorage.getItem("tableId")} })}
          sx={{ mt: 2, width: '100%' }}
          darkMode={darkMode}
        >
          Back to Orders
        </StyledButton>
      </StyledPaper>
    </Box>
  );
};

export default CashPayment;